import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { CampaignSegmentSchema } from '../campaign-segment/campaign-segment';
import { CampaignStatusSchema } from '../campaign-status/campaign-status';
import { PreferenceChannelSchema } from '../notification-channel/notification-channel';
import { NOTIFICATION_DELIVERY_STATUSES } from '../notification-delivery/notification-delivery';
import {
  TEMPLATE_BODY_MAX_LENGTH,
  TEMPLATE_TITLE_MAX_LENGTH,
} from '../notification-template/notification-template';
import { NotificationTemplateKeySchema } from '../notification-template-key/notification-template-key';

/** The operator's own label for the campaign. Never sent to anybody. */
export const CAMPAIGN_NAME_MAX_LENGTH = 160;

/**
 * A campaign's own copy, per locale (N5 — `campaigns` (content) is a translation
 * table, and `03-notifications.md` calls it "staff-composed content").
 *
 * Bounded by the **template** limits rather than by limits of its own, because
 * this copy is delivered through the template renderer: it becomes the `title`
 * and `body` parameters of the `announcements.campaign` template. Two ceilings
 * that could drift apart would drift apart the first time one was raised.
 */
export const CampaignContentSchema = z
  .object({
    title: z.string().trim().min(1).max(TEMPLATE_TITLE_MAX_LENGTH),
    body: z.string().trim().min(1).max(TEMPLATE_BODY_MAX_LENGTH),
  })
  .strict();

export type CampaignContent = z.infer<typeof CampaignContentSchema>;

/** Both locales required — U8's parity law, the same as template copy. */
export const CampaignContentTranslationsSchema = makeTranslationsSchema(CampaignContentSchema);

export type CampaignContentTranslations = z.infer<typeof CampaignContentTranslationsSchema>;

/**
 * A staff-composed announcement to a segment (`03-notifications.md` §Campaigns).
 *
 * ## The two kinds, and why one column carries both
 *
 * A campaign either **carries its own copy** (the operator writes it, and the
 * dispatch passes it as the parameters of `announcements.campaign`) or **names a
 * catalogue key** whose seeded copy already exists, with parameters supplied by
 * whatever created it. The second kind is what lets the legal re-consent notice
 * — a material publish, which must reach every member — be an ordinary campaign
 * with a preview and a delivery log, instead of a second population walk written
 * inside an event handler where nobody would look for it.
 *
 * `templateKey` is **not operator-writable**, and that is a containment property
 * rather than an omission: `bypassesPreferences` is a per-key catalogue fact, so
 * an operator who could choose the key could compose an announcement that
 * ignores every member's preferences. Staff campaigns are always
 * `announcements.campaign`, which does not bypass.
 */
export const CampaignSchema = z
  .object({
    id: UuidSchema,
    name: z.string(),
    templateKey: NotificationTemplateKeySchema,
    segment: CampaignSegmentSchema,
    status: CampaignStatusSchema,
    /** When the scheduler should claim it. Null means "send when told to". */
    scheduledAt: z.iso.datetime().nullable(),
    startedAt: z.iso.datetime().nullable(),
    sentAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    /**
     * How many recipients the walk actually **claimed** — null until it starts.
     *
     * Deliberately not the same number as the composer's preview: the segment is
     * resolved as the walk goes, so a member who registers between composing and
     * sending is included and one who deletes their account is not. The screen
     * says which of the two it is showing.
     */
    recipientCount: z.number().int().min(0).nullable(),
    /** Whether the operator wrote copy for this one, or it names seeded copy. */
    hasOwnContent: z.boolean(),
    /**
     * The channels this campaign travels on, or null for "every channel the key
     * declares" — the meaning every campaign composed before P21e carries.
     *
     * Preference channels only: a campaign is something a member may switch off,
     * so it can never name a channel that is not a choice.
     */
    channels: z.array(PreferenceChannelSchema).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Campaign = z.infer<typeof CampaignSchema>;

/**
 * How the campaign's deliveries are going, grouped by status.
 *
 * `03-notifications.md` asks for "progress + delivery stats in the admin
 * composer", and a campaign that reports only `sent` answers the least useful
 * half of the question — the operator opens this screen to find out whether the
 * push half is failing, which is a count per status or nothing.
 *
 * Written out rather than derived so the inferred type is exact;
 * `campaign.spec.ts` asserts the keys still equal
 * {@link NOTIFICATION_DELIVERY_STATUSES}, so adding a status fails a test rather
 * than silently dropping a column.
 */
export const CampaignDeliveryStatsSchema = z
  .object({
    queued: z.number().int().min(0),
    sending: z.number().int().min(0),
    sent: z.number().int().min(0),
    failed: z.number().int().min(0),
    dead: z.number().int().min(0),
  })
  .strict();

export type CampaignDeliveryStats = z.infer<typeof CampaignDeliveryStatsSchema>;

/** Every delivery status, for the spec that keeps the stats shape honest. */
export const CAMPAIGN_DELIVERY_STAT_KEYS = NOTIFICATION_DELIVERY_STATUSES;

/** What the composer reads: every locale (W7), plus how the walk is going. */
export const CampaignDetailSchema = CampaignSchema.extend({
  /** Null for a campaign that names seeded copy rather than carrying its own. */
  translations: CampaignContentTranslationsSchema.nullable(),
  deliveryStats: CampaignDeliveryStatsSchema,
}).strict();

export type CampaignDetail = z.infer<typeof CampaignDetailSchema>;
